import React, { Component } from 'react'
import { Popover, PopoverBody, PopoverHeader, Button, Input, Progress, Alert } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import Select from 'react-select'
import moment from 'moment'
import { debounce } from 'lodash'
import { RepsTypeAhead } from '../../TaskManagement/TaskComponents/TypeAheadComponents'
import RepAvatar from '../../Material/RepAvatar'
import './UpdateIncidentInfo.css'

import withApolloClient from '../../withApolloClient'
import DatetimePickerWithTimeZone from "../../Material/DatetimePickerWithTimeZone"
import { PreferredDateTimeContext } from "../../Context/PreferredDateTimeContext"
import { GetConfigurationSettings } from "../../Functions/querys"

const UpdateIncidentMutation = gql`
mutation UpdateIncidentMutation($input: UpdateIncidentMutationInput!) {
  updateIncident(input: $input) {
    ok
    errors {
      messages
    }
  }
}`

const UpdateIncidentAssignmentMutation = gql`
mutation UpdateIncidentAssignmentMutation($input: UpdateIncidentAssignmentMutationInput!) {
  updateIncidentAssignment(input: $input) {
    ok
    errors {
      messages
    }
  }
}`

const OpenIncidentTasksQuery = gql`
query OpenIncidentTasksQuery($incidentId: ID!, $filters: [FilterInput]) {
    allTasks(incidentId: $incidentId, filters: $filters) {
        edges {
            node {
                id
                title
                assignedTo {
                    edges {
                        node {
                            id
                            name
                            username
                            firstName
                            lastName
                        }
                    }
                }
            }
        }
    }
}`

const IncidentReportedByChoices = gql`
query IncidentReportedByChoices {
  allIncidentChoices: __type(name: "IncidentsIncidentReportedByChoices") {
    values: enumValues {
      name
      description
    }
  }
}`

const AllCarRentals = gql`query AllCarRentals($carPk: Int!, $activeOn:Date!){
    allRentals(carPk: $carPk, activeOn:$activeOn) {
        edges {
            node {
                id
                startDate
                endDate
                driver {
                    id
                    pk
                    name
                }
            }
        }
    }
}`

const AllDrivers = gql`query AllDrivers($searchTerm: String, $branchIds: [ID]){
    drivers: allDrivers(searchTerm: $searchTerm, branchIds: $branchIds, first: 10){
      edges{
        node{
          id
          name
          pk
          tlcLicense
          phone
          email
          branch{
            id
            name
          }
        }
      }
    }
}`

export class UpdateIncidentInfo extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: props.value || '',
            error: null,
            loading: false,
            driverOptions: [],
            driverLoading: false,
            assignmentCheckedTaskIds: [],
        }

        this.updateDriverSearchInput = debounce(this.fetchDriverOptions, 350)
    }

    // Tasks already assigned to exactly the reps currently selected to reassign to
    // are already in the desired state, so they're excluded from the "Open tasks" list.
    getReassignableTasks = () => {
        const tasks = (this.props.assignmentOpenTasksData?.edges || []).map((edge) => edge.node)
        const selectedIds = (this.state.value || []).map((rep) => rep.id).sort()
        if (!selectedIds.length) {
            return tasks
        }
        return tasks.filter((task) => {
            const taskAssigneeIds = (task?.assignedTo?.edges?.map((edge) => edge?.node?.id).filter(Boolean) || []).sort()
            return taskAssigneeIds.length !== selectedIds.length || taskAssigneeIds.some((id, idx) => id !== selectedIds[idx])
        })
    }
    
    componentDidMount() {
        if (this.props.name === 'driverId' && this.props.open) {
            this.fetchDriverOptions('')
        }
        if (this.props.name === 'assignedToIds' && this.props.assignmentOpenTasksData) {
            const tasks = this.getReassignableTasks()
            this.setState({ assignmentCheckedTaskIds: tasks.map((task) => task.id) })
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value || prevProps.open !== this.props.open) {
            this.setState({ value: this.props.value || '', error: null })
        }

        if (
            this.props.name === 'driverId' &&
            this.props.open &&
            (!prevProps.open || prevProps.branchId !== this.props.branchId)
        ) {
            this.fetchDriverOptions('')
        }

        if (
            this.props.name === 'assignedToIds' &&
            prevProps.assignmentOpenTasksData !== this.props.assignmentOpenTasksData &&
            this.props.assignmentOpenTasksData
        ) {
            const tasks = this.getReassignableTasks()
            this.setState({ assignmentCheckedTaskIds: tasks.map((task) => task.id) })
        }
    }

    componentWillUnmount() {
        if (this.updateDriverSearchInput?.cancel) {
            this.updateDriverSearchInput.cancel()
        }
    }

    fetchDriverOptions = (searchTerm = '') => {
        const { apolloClient, branchId } = this.props

        this.setState({ driverLoading: true })
        apolloClient.query({
            query: AllDrivers,
            variables: {
                searchTerm: searchTerm || null,
                branchIds: branchId ? [branchId] : null,
            },
            fetchPolicy: 'network-only',
        }).then((result) => {
            const driverOptions = (result?.data?.drivers?.edges || []).map(({ node }) => ({
                value: node.id,
                label: node.name || `Driver #${node.pk || node.id}`,
                pk: node.pk,
                phone: node.phone,
                email: node.email,
                tlcLicense: node.tlcLicense,
                branchName: node.branch?.name,
            }))
            this.setState({ driverOptions, driverLoading: false })
        }).catch(() => {
            this.setState({ driverOptions: [], driverLoading: false })
        })
    }

    toggleAssignmentTask = (taskId) => {
        this.setState((prevState) => ({
            assignmentCheckedTaskIds: prevState.assignmentCheckedTaskIds.includes(taskId)
                ? prevState.assignmentCheckedTaskIds.filter((id) => id !== taskId)
                : [...prevState.assignmentCheckedTaskIds, taskId],
        }))
    }

    toggleAllAssignmentTasks = () => {
        const tasks = this.getReassignableTasks()
        this.setState((prevState) => {
            const allChecked = tasks.length > 0 && prevState.assignmentCheckedTaskIds.length === tasks.length
            return { assignmentCheckedTaskIds: allChecked ? [] : tasks.map((task) => task.id) }
        })
    }

    confirmAssignment = () => {
        const { apolloClient, incidentId, refetchIncident, handleClose } = this.props
        const { value, assignmentCheckedTaskIds } = this.state
        const reassignableTaskIds = this.getReassignableTasks().map((task) => task.id)

        this.setState({ loading: true, error: null })
        apolloClient.mutate({
            mutation: UpdateIncidentAssignmentMutation,
            variables: {
                input: {
                    incidentId,
                    assignedToIds: (value || []).map((rep) => rep.id),
                    taskIds: assignmentCheckedTaskIds.filter((id) => reassignableTaskIds.includes(id)),
                },
            },
        }).then((response) => {
            if (response.data?.updateIncidentAssignment?.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data?.updateIncidentAssignment?.errors?.[0]?.messages.toString() || 'Update failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred. Please try again.' })
        })
    }

    handleUpdateIncident = () => {
        const { apolloClient, incidentId, name, refetchIncident, handleClose, metadata, type } = this.props
        const { value } = this.state

        this.setState({ loading: true })
        let input = { incidentId }

        if (metadata || type === "json") {
            const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})
            const additionalInfo = parsed.additional_info || {}
            const updatedAdditionalInfo = { ...additionalInfo, [name]: value }
            const updatedMetadata = { ...parsed, additional_info: updatedAdditionalInfo }
            input["metadata"] = JSON.stringify(updatedMetadata)
        } else {
            input[name] = value
        }

        apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: { input }
        }).then(response => {
            if (response.data.updateIncident.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data.updateIncident.errors[0]?.messages.toString() || 'Update failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred. Please try again.' })
        })
    }

    handleDeleteIncidentField = () => {
        const { apolloClient, incidentId, name, refetchIncident, handleClose, metadata } = this.props

        this.setState({ loading: true })

        const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})
        const additionalInfo = parsed.additional_info || {}
        delete additionalInfo[name]

        const updatedMetadata = { ...parsed, additional_info: additionalInfo }

        const input = {
            incidentId,
            metadata: JSON.stringify(updatedMetadata)
        }

        apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: { input }
        }).then(response => {
            if (response.data.updateIncident.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data.updateIncident.errors[0]?.messages.toString() || 'Delete failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred while deleting. Please try again.' })
        })
    }

    renderAssignmentPopover() {
        const { open, target, handleClose, title, assignmentOpenTasksLoading } = this.props
        const { value, loading, error, assignmentCheckedTaskIds } = this.state
        const tasks = this.getReassignableTasks()
        const allOpenTasks = (this.props.assignmentOpenTasksData?.edges || []).map((edge) => edge.node)
        const assignmentTasksLoading = !!assignmentOpenTasksLoading
        const selectedAssignees = value || []
        const allChecked = tasks.length > 0 && assignmentCheckedTaskIds.length === tasks.length

        return (
            <Popover popperClassName="incident-assignment-popover" placement="auto-start" isOpen={open} target={target} toggle={handleClose} trigger="legacy">
                <PopoverHeader onClick={handleClose}>{title || 'Edit Assigned To'}<i className="fa fa-times-circle" /></PopoverHeader>
                <PopoverBody>
                    {error && <Alert color="danger" className="iap-alert">{error}</Alert>}
                    <div className="iap-section">
                        <label className="iap-section-label">Reassign to</label>
                        <RepsTypeAhead onChange={(value) => this.setState({ value })} defaultValue={selectedAssignees} permissions={["custom_can_assign_to_incident"]} returnObjects />
                    </div>

                    {!assignmentTasksLoading && tasks.length === 0 && (
                        <p className="iap-loading">
                            {(allOpenTasks.length > 0 && tasks.length === 0) ? "All open tasks are already assigned to the selected rep." : "There are no open tasks, or you don't have access to the board they're on."}
                        </p>
                    )}
                    {!assignmentTasksLoading && tasks.length > 0 && (
                        <div className="iap-section">
                            <div className="iap-tasks-header">
                                <label className="iap-section-label iap-tasks-heading-label">Open tasks</label>
                                <span className="bos-badge bos-badge-blue">{tasks.length}</span>
                                <label className="iap-select-all">
                                    <input type="checkbox" aria-label="select all" checked={allChecked} onChange={this.toggleAllAssignmentTasks} />
                                    <span>{allChecked ? "Deselect all" : "Select all"}</span>
                                </label>
                            </div>
                            <p className="iap-tasks-hint">Choose which ones should also move to the new assignee.</p>
                            <div className="iap-task-list">
                                {tasks?.map((task) => (
                                    <label key={task.id} className={`iap-task-row${assignmentCheckedTaskIds.includes(task.id) ? " is-checked" : ""}`}>
                                        <input type="checkbox" aria-label={`include ${task.title}`} checked={assignmentCheckedTaskIds.includes(task.id)} onChange={() => this.toggleAssignmentTask(task.id)} />
                                        <span className="iap-task-title">{task.title}</span>
                                        <span className="iap-task-flow">
                                            {(task?.assignedTo?.edges?.map((edge) => edge?.node).filter(Boolean) || []).map((rep) => <RepAvatar key={rep.id} rep={rep} />)}
                                            <i className="fa fa-arrow-right iap-arrow" aria-hidden="true" />
                                            {selectedAssignees.length ? selectedAssignees.map((rep) => <RepAvatar key={rep.id} rep={rep} />) : <RepAvatar rep={null} text="--" />}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {loading || assignmentTasksLoading ? (
                        <Progress animated color="info" value={100} className="mt-3" />
                    ) : (
                        <div className="iap-footer">
                            <Button color="link" size="sm" className="text-muted" onClick={handleClose}>Cancel</Button>
                            <Button color="primary" size="sm" onClick={this.confirmAssignment}>Confirm</Button>
                        </div>
                    )}
                </PopoverBody>
            </Popover>
        )
    }

    render() {
        const { open, target, handleClose, title, name, allIncidentChoices, type, min, max } = this.props
        const { value, loading, error } = this.state

        if (name === 'assignedToIds') {
            return this.renderAssignmentPopover()
        }

        const choices = allIncidentChoices?.values ? allIncidentChoices.values.map(item => ({ value: item.name, label: item.description })) : []
        const inputFieldConfig = this.props.incidentMetadataColumnConfigs?.find(f => f.key === name)

        const rentalOptions = (this.props.rentals?.edges || []).map(({ node }) => ({
            value: node.id,
            pk: node.driver.pk ?? node.driver.id,
            name: node.driver.name || 'Unknown',
            start: moment(node.startDate).format('YYYY-MM-DD'),
            end: node.endDate ? moment(node.endDate).format('YYYY-MM-DD') : "ACTIVE",
        }))
        const currentDriverOption = this.props.currentOption && this.props.value ? {
            value: this.props.value,
            ...this.props.currentOption,
        } : null
        const selectedDriverOption = this.state.driverOptions.find((opt) => opt.value === (this.state.value || this.props.value))
            || currentDriverOption
        return (
            <Popover placement="auto-start" isOpen={open} target={target} toggle={handleClose} trigger="legacy" style={{ minWidth: '250px' }}>
                <PopoverHeader onClick={handleClose}>{title}&nbsp;&nbsp;<i className="fa fa-times-circle float-right" /></PopoverHeader>
                <PopoverBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <div className="pb-2">
                        {name === 'notificationDatetime' ? (
                            <PreferredDateTimeContext.Consumer>
                                {({ preferredDateTimeInfo }) => {
                                    const tz = this.props.branchTimezone || preferredDateTimeInfo.timezone
                                    return (
                                        <DatetimePickerWithTimeZone
                                            key={tz}
                                            type={type || 'datetime-local'}
                                            name={name}
                                            timezone={tz}
                                            value={value}
                                            min={min ? moment(min).tz(tz).format(moment.HTML5_FMT.DATETIME_LOCAL) : null}
                                            max={max ? moment(max).tz(tz).format(moment.HTML5_FMT.DATETIME_LOCAL) : null}
                                            setError={(error) => this.setState({ error })}
                                            updateInput={(name, value) => this.setState({ value: value })}
                                        />
                                    )
                                }}
                            </PreferredDateTimeContext.Consumer>
                        ) :
                            name === 'rentalId' ? (
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        options={rentalOptions}
                                        value={rentalOptions?.find(opt => opt.value === (this.state.value || this.props.value)) || null}
                                        formatOptionLabel={(opt) => (<><p className="m-0"><small>({opt.pk})</small> {opt.name}</p><small>{opt.start} — {opt.end}</small></>)}
                                        placeholder="Select Rental Driver"
                                        isLoading={this.props.allRentalsLoading}
                                        onChange={(opt) => this.setState({ value: opt?.value || '' })}
                                    />
                                ) : name === 'driverId' ? (
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        options={this.state.driverOptions}
                                        value={selectedDriverOption || null}
                                        placeholder="Search Driver"
                                        isLoading={this.state.driverLoading}
                                        onMenuOpen={() => {
                                            if (!this.state.driverOptions.length) {
                                                this.fetchDriverOptions('')
                                            }
                                        }}
                                        onInputChange={(inputValue, actionMeta) => {
                                            if (actionMeta.action === 'input-change') {
                                                this.updateDriverSearchInput(inputValue)
                                            }
                                        }}
                                        onChange={(opt) => this.setState({ value: opt?.value || '' })}
                                        formatOptionLabel={(opt) => (
                                            <>
                                                <p className="m-0">
                                                    <small>({opt.pk || '--'})</small> {opt.label}
                                                </p>
                                                <small>
                                                    {[opt.phone, opt.tlcLicense, opt.branchName].filter(Boolean).join(' • ') || opt.email || 'No extra details'}
                                                </small>
                                            </>
                                        )}
                                        noOptionsMessage={() => this.state.driverLoading ? 'Loading drivers...' : 'No drivers found'}
                                        isClearable
                                    />
                                ) :
                                    name === 'reportedBy' ? (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            options={choices}
                                            defaultValue={choices.find(opt => opt.value === value)}
                                            onChange={(opt) => this.setState({ value: opt.value })}
                                        />
                                    ) : type === "json" && inputFieldConfig?.dataType === 'enum' ? (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            options={inputFieldConfig.options.map(opt => ({ value: opt, label: opt }))}
                                            defaultValue={{ value: value, label: value }}
                                            onChange={(opt) => this.setState({ value: opt.value })}
                                        />
                                    ) : type === "json" && inputFieldConfig?.dataType === 'bool' ? (
                                        <div className="d-flex align-items-center">
                                            <span >Yes</span>
                                            <div className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    className="toggle-switch-checkbox"
                                                    id={`bool-toggle-${name}`}
                                                    checked={!value}
                                                    onChange={(e) => this.setState({ value: !e.target.checked })}
                                                />
                                                <label className="toggle-switch-label" htmlFor={`bool-toggle-${name}`}>
                                                    <span className="toggle-switch-inner" />
                                                    <span className="toggle-switch-switch" />
                                                </label>
                                            </div>
                                            <span >No</span>
                                        </div>
                                    ) :
                                        (<Input
                                            type={type || 'text'}
                                            name={name}
                                            value={value}
                                            onChange={(e) => this.setState({ value: e.target.value })}
                                        />
                                        )}

                        {loading ? (
                            <Progress animated color="info" value={100} />
                        ) : (
                            <div className="d-flex justify-content-between mt-3">
                                {this.props.showDelete && (
                                    <Button color="danger" size="sm" onClick={this.handleDeleteIncidentField}> Delete </Button>
                                )}
                                <Button color="primary" size="sm" onClick={this.handleUpdateIncident}> Submit</Button>
                            </div>
                        )}
                    </div>
                </PopoverBody>
            </Popover>
        )
    }
}

export default graphql(IncidentReportedByChoices, {
    options: { fetchPolicy: 'cache-first' },
    props: ({ data: { allIncidentChoices } }) => ({ allIncidentChoices }),
})(
    graphql(AllCarRentals, {
        options: ({ carPk, incidentDate }) => ({
            variables: { carPk, activeOn: incidentDate },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            skip: !carPk || !incidentDate,
        }),
        props: ({ data }) => ({
            rentals: data?.allRentals,
            allRentalsLoading: data?.loading,
        }),
    })(
        graphql(GetConfigurationSettings, {
            skip: (props) => !props.branchId,
            options: (props) => ({
                variables: { branchId: props.branchId, name: "PREFERRED_TIMEZONE" },
                fetchPolicy: "cache-first",
            }),
            props: ({ data: { getConfigurationSettings } }) => ({
                branchTimezone: getConfigurationSettings,
            }),
        })(
            graphql(OpenIncidentTasksQuery, {
                skip: (props) => props.name !== 'assignedToIds',
                options: (props) => ({
                    variables: { 
                        incidentId: props.incidentId, 
                        filters: [{ fieldName: "is_archived", filterType: "equal", filterValues: "false" }, { fieldName: "status", filterType: "in", filterValues: ["OPEN", "IN_PROGRESS"] }] 
                    },
                    fetchPolicy: 'network-only',
                }),
                props: ({ data }) => ({
                    assignmentOpenTasksData: data?.allTasks,
                    assignmentOpenTasksLoading: data?.loading,
                }),
            })(withApolloClient(UpdateIncidentInfo))
        )
    )
)
